import { Box, Chip, Grid, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  IconCampaignList,
  IconCircleArrowLeft,
  IconCircleArrowRight,
} from '@shared/assets/images/svgs';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { CampaignType } from '@shared/services/apiService/apiService';
import { useAppSelector } from '@shared/store';
import { firstSymbolToUpperCase } from './utils';

interface BreadCrumbProps {
  subtitle?: string;
  title: string;
  showBackButton?: boolean;
  campaignType?: CampaignType | null;
  icon?: string | null;
}

const iconMap = {
  IconCampaignList,
};

const IconRenderer = (iconName: keyof typeof iconMap) => {
  const Icon = iconMap[iconName];
  return Icon ? <Icon style={{ marginRight: '12px' }} /> : null;
};

const HeaderChip = styled(Chip)(({ theme }) => ({
  height: '23px',
  marginLeft: '12px',
  fontSize: '12px',
  fontWeight: 600,
  '.MuiChip-label': {
    padding: '0 10px 0 6px',
    lineHeight: 'normal',
  },
  '& svg': {
    height: '14px',
    width: '14px',
  },
}));

const BreadcrumbV2 = ({ subtitle, title, campaignType, icon }: BreadCrumbProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth.authenticatedUser);
  const isViewer = currentUser?.role === UserRolesEnum.VIEWER;

  const getCampaignChipLabel = (campaignType: CampaignType) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          flexDirection: 'row',
        }}
        gap={'4px'}>
        {campaignType === CampaignTypeEnum.INBOUND ? (
          <IconCircleArrowLeft />
        ) : (
          <IconCircleArrowRight />
        )}
        {firstSymbolToUpperCase(campaignType)}
      </Box>
    );
  };
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sm={6}
        lg={8}
        mb={1}
        sx={{
          display: 'flex',
          margin: 0,
          alignItems: 'center',
        }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '21px',
            fontWeight: 600,
            paddingLeft: '15px',
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          {IconRenderer(icon as keyof typeof iconMap)}
          {title}
          {campaignType && (
            <HeaderChip
              sx={{
                backgroundColor:
                  campaignType === CampaignTypeEnum.OUTBOUND
                    ? theme.palette.secondary.light
                    : theme.palette.error.light,
                color: campaignType === CampaignTypeEnum.OUTBOUND ? '#1875FF' : '#FA896B',
              }}
              label={getCampaignChipLabel(campaignType)}
            />
          )}
          {isViewer ? (
            <Tooltip title={t('common.viewerModeTooltip')} arrow>
              <HeaderChip
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  color: theme.palette.text.secondary,
                  '.MuiChip-label': {
                    padding: '0 10px',
                  },
                }}
                label={t('common.viewerMode')}
              />
            </Tooltip>
          ) : null}
        </Typography>
        {subtitle ? (
          <Typography color="textSecondary" variant="h6" fontWeight={400} mt={0.8} mb={0}>
            {subtitle}
          </Typography>
        ) : null}
      </Grid>
    </Grid>
  );
};

export default BreadcrumbV2;
